
import { Injectable } from '@angular/core';
import { auditTime, delay, distinctUntilChanged, filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { ServerResponse } from '@app/enums/server-response';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { LobbyService } from '../../../features/lobby/services/lobby.service';

import * as LobbyActions from '../lobby/lobby.actions';
import * as TournamentSummariesActions from '../tournament-summaries/tournament-summaries.actions';
import { ServerMessageType } from '@app/enums/server-message-type';

import { Store, select } from '@ngrx/store';

import * as TablesSelectors from '../tables/tables.selector';

import * as TournamenstSelectors from './tournaments.selector';

import * as TournamenstActions from './tournaments.actions';
import { WsService } from '@app/services/ws.service';
import { TournamentSummaryResponse } from '@app/models/tournament-summary';
import { TournamentInfo, TournamentInformationResponse } from '@app/models/tournament-information';
import { BlindInformation } from '@app/models/blind-information';
import { pl } from 'date-fns/locale';
import { TournamentActiveTable } from '@app/models/tournament-active-table';
import { combineLatest, of } from 'rxjs';
import { TournamentInfoPlayer } from '@app/models/tournament-info-player';
import { isEqual } from 'lodash';

import * as UserSelectors from '@app/store/features/user/user.selectors';
import * as CurrenciesSelectors from '@app/store/features/currencies/currencies.selectors';
import * as PlayerBalanceSelectors from '@app/store/features/player-balance/player-balance.selectors';
import { ServerMsgAskPlayerRegisterToNextSNG, ServerMsgConnectResponse, ServerMsgRegisterToNextSNGResponse, ServerMsgTournamentError } from '@app/models/server-msg';
import { ErrorCode } from '@app/enums/errors';
import { LiveLobbyTournament, TourGameData } from '@app/models/live-lobby-tournament';
import { addSeconds } from 'date-fns';
import { Dialog } from '@angular/cdk/dialog';
import { TournamentDialogAskPlayerRegisterToNextComponent, TournamentDialogAskPlayerRegisterToNextData } from '@app/features/tournament/presenters/tournament-dialog-ask-player-register-to-next/tournament-dialog-ask-player-register-to-next.component';
import { LobbyFilterTableType } from '@app/features/lobby/enums/lobby-filter-table-type';
import { Blind, BlindType } from '@app/models/blind';
import * as ConfigSelectors from '@app/store/features/config/config.selectors';

@Injectable()
export class TournamentsEffects {
    constructor(
        private _dialog: Dialog,
        private _ws: WsService,
        private readonly _store: Store,
        private readonly lobbyService: LobbyService,
        private actions$: Actions,

    ) { }


    tournamentError$ = createEffect(() =>
        this._ws.getServerMsg<ServerMsgTournamentError>(ServerMessageType.Error)
            .pipe(
                filter(error => [ErrorCode.TournamentDoNotExist].indexOf(error.errorCode) > -1),
                map(error => TournamenstActions.upsertOne({ tournament: { id: error.idTournament, errorCode: error.errorCode } }))
            ));


    onSelectTournament$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(LobbyActions.onSelectTournament),
                tap(({ tournamentSummary }) => {
                    this.lobbyService.getTournamentSummary(tournamentSummary.id);
                    this.lobbyService.getTournamentInfo(tournamentSummary.id);
                    this.lobbyService.getBlindSchedule(tournamentSummary.id);

                }),
                map(({ tournamentSummary }) => TournamenstActions.upsertOne({ tournament: { id: tournamentSummary.id, tournamentSummary } }))
            ));

    onSelectSitNGo$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(LobbyActions.onSelectSitNGo),
                tap(({ sitNGoSummary }) => {
                    this.lobbyService.getTournamentSummary(sitNGoSummary.id);
                    this.lobbyService.getTournamentInfo(sitNGoSummary.id);
                    this.lobbyService.getBlindSchedule(sitNGoSummary.id);

                }),
                map(({ sitNGoSummary }) => TournamenstActions.upsertOne({ tournament: { id: sitNGoSummary.id, sitNGoSummary } }))
            ));

    onSelectSpinNGo$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(LobbyActions.onSelectSpinNGo),
                tap(({ spinNGoSummary }) => {
                    this.lobbyService.getTournamentSummary(spinNGoSummary.id);
                    this.lobbyService.getTournamentInfo(spinNGoSummary.id);
                    this.lobbyService.getBlindSchedule(spinNGoSummary.id);

                }),
                map(({ spinNGoSummary }) => TournamenstActions.upsertOne({ tournament: { id: spinNGoSummary.id, spinNGoSummary } }))
            ));

    updateTournamentSummary$ = createEffect(() =>
        this._ws.getDataResponse<TournamentSummaryResponse>(ServerResponse.TournamentSummary)
            .pipe(
                map(tournamentSummaryResponse => {
                    const tournamentSummary = this.lobbyService.updateTournamentSummary(tournamentSummaryResponse)
                    return TournamenstActions.upsertOne({ tournament: { id: tournamentSummaryResponse.id, tournamentSummary } })
                }))
    );

    updateTournamentInformationResponse$ = createEffect(() =>
        this._ws.getDataResponse<TournamentInformationResponse>(ServerResponse.TournamentInformation)
            .pipe(
                delay(200), //wait for tournament info to be processed
                auditTime(120), //collapse bursts: keep only the last event every 120ms
                distinctUntilChanged((a, b) => isEqual(a, b)),

                switchMap((tournamentInformationResponse) => {
                    return combineLatest([
                        this._store.pipe(select(UserSelectors.selectUserProfile), take(1)),
                        this._store.pipe(select(TournamenstSelectors.selectEntityById(tournamentInformationResponse.idTournament)), take(1))
                    ]).pipe(

                        map(([userProfile, tournament]) => {

                            /**
                             * @version 0.0.1
                             * move to adapter service
                             */

                            let prizesColumns: string[] = tournament?.tournamentInfo?.game.prizesColumns ?? [];
                            let prizes: any = tournament?.tournamentInfo?.game.prizes ?? [];
                            if (tournamentInformationResponse.prizes) {
                                prizes = tournamentInformationResponse.prizes.sort((a, b) => a.position - b.position > 0 ? 1 : -1);
                            }
                            if (tournamentInformationResponse?.prizes2) {
                                prizes = tournamentInformationResponse.prizes2.sort((a, b) => a.position - b.position > 0 ? 1 : -1);

                                const columns: any = {};

                                for (const item of prizes) {
                                    if (item.position) {
                                        columns['Position'] = true;
                                    }
                                    if (item.chips && item.chips.percentage) {
                                        columns['ChipsPercentage'] = true;
                                    }
                                    if (item.chips && item.chips.amount) {
                                        columns['ChipsAmount'] = true;
                                    }
                                    if (item.tickets) {
                                        columns['Tickets'] = true;
                                    }
                                    if (item.externalTickets) {
                                        columns['ExternalTickets'] = true;
                                    }
                                    if (item.externalPoints) {
                                        columns['Points'] = true;
                                    }
                                }
                                prizesColumns = Object.keys(columns);

                            }

                            let highestStack = 0
                            let lowestStack = 0
                            let averageStack = 0

                            if (tournamentInformationResponse.players.length > 0) {
                                const numberOfChips = tournamentInformationResponse.players
                                    .filter(player => player.nbChips > 0)
                                    .map(player => player.nbChips);

                                highestStack = Math.max(...numberOfChips);
                                lowestStack = Math.min(...numberOfChips);
                                averageStack = Math.round(numberOfChips.reduce((total, num) => total + num, 0)) / numberOfChips.length
                            }



                            // #######  Players START
                            // MEMORY LEAK FIX: Fully immutable approach - no mutations

                            let tournamentPlayers: TournamentInfoPlayer[];

                            if (tournamentInformationResponse.nbPlayerRegistered === tournamentInformationResponse.players.length) {
                                // Full refresh - create new array from response
                                tournamentPlayers = tournamentInformationResponse.players.map(player => ({
                                    name: player.name,
                                    idPlayer: player.idPlayer,
                                    nbChips: player.nbChips ?? 0,
                                    idTable: player.idTable,
                                    positionEnded: player.positionEnded,
                                    endingPrice: player.endingPrice,
                                    bounty: player.bounty ?? 0,
                                    bountyEarned: player.bountyEarned ?? 0,
                                    nbRebuy: player.nbRebuy ?? 0
                                }));
                            } else {
                                // Partial update - merge existing with new players immutably
                                const existingPlayers = tournament?.tournamentInfo?.players || [];
                                const playersMap = new Map<number, TournamentInfoPlayer>();

                                // Add existing players to map
                                existingPlayers.forEach(player => {
                                    playersMap.set(player.idPlayer, player);
                                });

                                // Update or add new players
                                tournamentInformationResponse.players.forEach(player => {
                                    playersMap.set(player.idPlayer, {
                                        name: player.name,
                                        idPlayer: player.idPlayer,
                                        nbChips: player.nbChips ?? 0,
                                        idTable: player.idTable,
                                        positionEnded: player.positionEnded,
                                        endingPrice: player.endingPrice,
                                        bounty: player.bounty ?? 0,
                                        bountyEarned: player.bountyEarned ?? 0,
                                        nbRebuy: player.nbRebuy ?? 0
                                    });
                                });

                                // Convert map to array
                                tournamentPlayers = Array.from(playersMap.values());
                            }

                            // ###### Players END
                            const myPlayer = tournamentInformationResponse.players.find(player => player.idPlayer === userProfile?.id);
                            const imOnTour = !myPlayer?.positionEnded ? myPlayer : undefined;
                            const iWasOnTour = myPlayer?.positionEnded ? myPlayer : undefined;
                            const isRegistered = !!(imOnTour && !imOnTour.positionEnded);


                            // #######  active tables START
                            // MEMORY LEAK FIX: Completely immutable approach - no mutations

                            let activeTables: TournamentActiveTable[] = [];

                            if (tournamentInformationResponse.nbPlayerRegistered !== tournamentInformationResponse.players.length) {
                                // Build tables map for efficient processing
                                const tablesMap = new Map<number, TournamentActiveTable>();

                                // Initialize from existing tables if available
                                if (tournament?.activeTables) {
                                    tournament.activeTables.forEach(table => {
                                        tablesMap.set(table.idTable, {
                                            ...table,
                                            playersList: [] // Will rebuild player list
                                        });
                                    });
                                }

                                // Process all active players (not eliminated, with valid table ID)
                                const activePlayers = tournamentInformationResponse.players.filter(
                                    player => !player.positionEnded && player.idTable !== 0
                                );

                                for (const player of activePlayers) {
                                    const newPlayer: TournamentInfoPlayer = {
                                        name: player.name,
                                        idPlayer: player.idPlayer,
                                        nbChips: player.nbChips ?? 0,
                                        idTable: player.idTable,
                                        positionEnded: player.positionEnded,
                                        endingPrice: player.endingPrice,
                                        bounty: player.bounty ?? 0,
                                        bountyEarned: player.bountyEarned ?? 0,
                                        nbRebuy: player.nbRebuy ?? 0
                                    };

                                    const existingTable = tablesMap.get(player.idTable);

                                    if (existingTable) {
                                        // Add player to existing table's players list
                                        existingTable.playersList = [...existingTable.playersList, newPlayer];
                                    } else {
                                        // Create new table
                                        tablesMap.set(player.idTable, {
                                            idTable: player.idTable,
                                            minChips: player.nbChips,
                                            maxChips: player.nbChips,
                                            playersList: [newPlayer],
                                            numberOfPlayers: 1,
                                            isMyTable: player.idPlayer === userProfile!.id
                                        });
                                    }
                                }

                                // Recalculate stats for each table and create final immutable array
                                activeTables = Array.from(tablesMap.values())
                                    .filter(table => table.playersList.length > 0) // Remove empty tables
                                    .map(table => {
                                        const tableChips = table.playersList.map(p => p.nbChips);
                                        const isMyTable = table.playersList.some(p => p.idPlayer === userProfile!.id);

                                        return {
                                            ...table,
                                            playersList: table.playersList.sort((a, b) => b.nbChips - a.nbChips),
                                            maxChips: Math.max(...tableChips, 0),
                                            minChips: Math.min(...tableChips, 0),
                                            numberOfPlayers: table.playersList.length,
                                            isMyTable
                                        };
                                    })
                                    .sort((a, b) => b.idTable - a.idTable);
                            }
                            // #######  active tables END





                            // MEMORY LEAK FIX: Create deep copy instead of spreading response
                            let tournamentInfo: TournamentInfo = {
                                idTournament: tournamentInformationResponse.idTournament,
                                nbPlayerRegistered: tournamentInformationResponse.nbPlayerRegistered,
                                nbRebuyUsed: tournamentInformationResponse.nbRebuyUsed,
                                nbAddonUsed: tournamentInformationResponse.nbAddonUsed,
                                totalPrizePool: tournamentInformationResponse.totalPrizePool,
                                prizes: tournamentInformationResponse.prizes ? [...tournamentInformationResponse.prizes] : [],
                                prizes2: tournamentInformationResponse.prizes2 ? [...tournamentInformationResponse.prizes2] : [],

                                game: {
                                    firstPrize: prizes[0] ? { ...prizes[0] } : undefined,
                                    prizesColumns: [...prizesColumns],
                                    prizes: prizes.map((p: any) => ({ ...p })),
                                    stack: {
                                        highest: highestStack,
                                        lowest: lowestStack,
                                        average: averageStack
                                    }

                                },
                                players: tournamentPlayers.sort((a, b) => {
                                    const aHasPosition = a.positionEnded != null;
                                    const bHasPosition = b.positionEnded != null;

                                    // If both have positionEnded → sort ascending by positionEnded
                                    if (aHasPosition && bHasPosition) {
                                        return a.positionEnded - b.positionEnded;
                                    }

                                    // If neither has positionEnded → sort descending by nbChips
                                    return b.nbChips - a.nbChips;
                                })
                            }







                            return TournamenstActions.upsertOne({
                                tournament: {
                                    id: tournamentInformationResponse.idTournament,
                                    tournamentInfo,
                                    activeTables,
                                    isRegistered,
                                    tournamentSummary: tournament?.tournamentSummary
                                }
                            })
                        })
                    )
                })
            )
    );

    updateBlindInformation$ = createEffect(() =>
        this._ws.getDataResponse<BlindInformation>(ServerResponse.BlindInformation)
            .pipe(

                switchMap((blindInformation) => {
                    return combineLatest([
                        of(blindInformation),
                        this._store.pipe(select(TournamenstSelectors.selectEntityById(blindInformation.idTournament)), filter(data => !!data && !!data.tournamentSummary), map(data => data!), take(1)),
                        this._store.pipe(
                            select(ConfigSelectors.getGlobalSettings),
                            filter(globalSettings => !!globalSettings),
                            map(globalSettings => globalSettings!),
                            take(1)
                        )
                    ])
                }),
                map(([blindInformation, tournament, globalSettings]) => {

                    const { periodDuration,
                        addonBreakOn,
                        breakEvery,
                        lateRegistrationPeriod,
                        reBuyPeriod,
                        optionSyncronizedBreak } = tournament.tournamentSummary!;


                    const blinds: Blind[] = [];

                    let index = 0;
                    for (const blind of blindInformation.blinds) {
                        blinds.push({
                            type: BlindType.Blind,
                            id: blinds.length + 1,
                            level: index + 1,
                            blind: blind.blind,
                            ante: blind.ante,
                            startTime: blind.periodStartMinutes,
                            duration: periodDuration,

                            period: blind.period,
                            periodStartMinutes: blind.periodStartMinutes,

                            variant: blind?.variant
                        });
                        const nextEventTime = blind.periodStartMinutes + periodDuration;

                        if (lateRegistrationPeriod === index + 1) {
                            blinds.push({
                                id: blinds.length + 1,
                                type: BlindType.LateRegistrationPeriod,
                                startTime: nextEventTime,
                                duration: 0,

                                level: 0,
                                blind: 0,
                                period: 0,
                                periodStartMinutes: 0
                            });
                        }
                        if (reBuyPeriod === index + 1) {
                            blinds.push({
                                id: blinds.length + 1,
                                type: BlindType.ReBuyPeriod,
                                startTime: nextEventTime,
                                duration: 0,

                                level: 0,
                                blind: 0,
                                period: 0,
                                periodStartMinutes: 0
                            });
                        }
                        if (addonBreakOn === index + 1) {
                            blinds.push({
                                id: blinds.length + 1,
                                type: BlindType.AddonBreakOn,
                                startTime: nextEventTime,
                                duration: globalSettings.addOnBreakDuration,

                                level: 0,
                                blind: 0,
                                period: 0,
                                periodStartMinutes: 0
                            });
                        }

                        if (
                            !optionSyncronizedBreak &&
                            breakEvery > 0 &&
                            (index + 1) % breakEvery === 0
                        ) {
                            blinds.push({
                                id: blinds.length + 1,
                                type: BlindType.Break,
                                startTime: nextEventTime,
                                duration: globalSettings.breakDuration,

                                level: 0,
                                blind: 0,
                                period: 0,
                                periodStartMinutes: 0
                            });
                        }
                        index++;
                    }


                    return TournamenstActions.upsertOne({ tournament: { id: blindInformation.idTournament, blinds } })
                }))
    );


    liveLobbyTournament$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyTournament>(ServerResponse.LiveLobbyTournament)
            .pipe(
                map(liveLobbyTournament => {

                    // MEMORY LEAK FIX: Destructure to avoid storing reference to response
                    const { summary, ...liveLobbyTourGame } = liveLobbyTournament;
                    const tournamentSummary = this.lobbyService.updateTournamentSummary(summary);

                    // MEMORY LEAK FIX: Create explicit copy of all properties
                    const gameData: TourGameData = {
                        // TournamentSummaryBasic properties
                        startIn: liveLobbyTourGame.startIn,
                        registrationsStartIn: liveLobbyTourGame.registrationsStartIn,
                        registrationEndIn: liveLobbyTourGame.registrationEndIn,
                        blindIncreaseIn: liveLobbyTourGame.blindIncreaseIn,
                        breakIn: liveLobbyTourGame.breakIn,
                        breakEndIn: liveLobbyTourGame.breakEndIn,
                        endedSince: liveLobbyTourGame.endedSince,
                        reBuyEndIn: liveLobbyTourGame.reBuyEndIn,

                        nextRotationIn: liveLobbyTourGame.nextRotationIn,
                        nextVariant: liveLobbyTourGame.nextVariant,

                        // TourGameData specific properties
                        idTournament: liveLobbyTourGame.idTournament,
                        status: liveLobbyTourGame.status,
                        status2: liveLobbyTourGame.status2,
                        nbPlayersRegistered: liveLobbyTourGame.nbPlayersRegistered,
                        nbPlayersActive: liveLobbyTourGame.nbPlayersActive,
                        fallGuysCurrentLevel: liveLobbyTourGame.fallGuysCurrentLevel,
                        fallGuysTotalLevels: liveLobbyTourGame.fallGuysTotalLevels,
                        fallGuysNextLevelStartsWith: liveLobbyTourGame.fallGuysNextLevelStartsWith,
                        blind: liveLobbyTourGame.blind,
                        ante: liveLobbyTourGame.ante,
                        nextBlind: liveLobbyTourGame.nextBlind,
                        nextAnte: liveLobbyTourGame.nextAnte,
                        isNewTournament: liveLobbyTourGame.isNewTournament
                    };

                    // MEMORY LEAK FIX: Reuse Date base to avoid creating excessive objects
                    const now = new Date();
                    if (gameData.blindIncreaseIn) {
                        gameData.blindIncreaseInDate = addSeconds(now, gameData.blindIncreaseIn);
                    }

                    if (gameData.breakEndIn) {
                        gameData.breakEndInDate = addSeconds(now, gameData.breakEndIn);
                    }

                    if (gameData.breakIn) {
                        gameData.breakInDate = addSeconds(now, gameData.breakIn);
                    }

                    if (gameData.nextRotationIn) {
                        gameData.nextRotationInDate = addSeconds(now, gameData.nextRotationIn);
                    }


                    return TournamenstActions.upsertOne({ tournament: { id: tournamentSummary.id, tournamentSummary, gameData } })
                })
            )
    );


    //

    onViewTournament$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(TournamenstActions.viewTournament),
                tap(({ tournamentId }) => {
                    this.lobbyService.getTournamentSummary(tournamentId);
                    this.lobbyService.getTournamentInfo(tournamentId);
                    this.lobbyService.getBlindSchedule(tournamentId);
                }),
            ), { dispatch: false });



    //

    onRegisterTournament$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(TournamenstActions.registerTournament),
                tap(({ tournamentId, register }) => this.lobbyService.tournamentRegister(tournamentId, register)),
            ), { dispatch: false });



    askPlayerRegisterToNextSNG$ = createEffect(() =>
        this._ws.getServerMsg<ServerMsgAskPlayerRegisterToNextSNG>(ServerMessageType.AskPlayerRegisterToNextSNG)
            .pipe(
                switchMap((msg) => {
                    return combineLatest([
                        of(msg),
                        this._store.pipe(
                            select(CurrenciesSelectors.selectActive),
                            filter(currency => !!currency),
                            map(currency => currency!),
                            take(1)
                        ),
                        this._store.pipe(
                            select(PlayerBalanceSelectors.selectEntities),
                            filter(el => !!el),
                            map(el => el!),
                            take(1)
                        ),
                        this._store.pipe(
                            select(UserSelectors.selectUserProfile),
                            filter(el => !!el),
                            map(el => el!),
                            take(1)
                        ),
                    ])
                }),
                filter(([msg, currency, playerBalances, userProfile]) => msg.idPlayer === userProfile.id),
                switchMap(([msg, currency, playerBalances, userProfile]) => {
                    const playerBalance = playerBalances[currency.id];
                    const tournamentTemplateId = msg.value;
                    const entryFee = msg.value2;
                    const houseFee = msg.value3;


                    const dialog = this._dialog.open<boolean>(TournamentDialogAskPlayerRegisterToNextComponent, {
                        width: '300px',
                        data: { playerBalance, entryFee, houseFee, currency }
                    })

                    return combineLatest([of(tournamentTemplateId), dialog.closed]);
                }),
                tap(([tournamentTemplateId, playAgain]) => {
                    if (playAgain) {
                        this.lobbyService.tournamentPlayAgain(tournamentTemplateId);
                    }
                })
            ), {
        dispatch: false
    });

    registerToNextSNGResponse$ = createEffect(() =>
        this._ws.getServerMsg<ServerMsgRegisterToNextSNGResponse>(ServerMessageType.RegisterToNextSNGResponse)
            .pipe(
                filter((msg) => msg.value > -1), // CODE_DEBT: handle response with error when registration fails
                map((msg) => {
                    const tableType: LobbyFilterTableType = msg.value2;
                    return LobbyActions.updateActiveNav({ activeNav: tableType })
                }),
            ));
}